import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { 
  GasPlantCapacitiesRequest, 
  GasPlantCapacitiesResponse,
  GasPlantCapacitiesDetailRequest,
  GasPlantCapacitiesDetailResponse,
  GasPlantCapacity,
  SaveGasPlantCapacityRequest,
  DeleteGasPlantCapacityRequest,
  GasPlantCapacityRow,
  DropdownColumn,
  GasPlantCapacitiesCascadingFilterRequest,
  GasPlantCapacitiesCascadingFilterResponse,
  FilterColumn
} from './interfaces/gas-plant-capacities.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';

@Injectable({
  providedIn: 'root',
})
export class GasPlantCapacitiesService {
  private baseUrl = environment.WebApiURL;
  private readonly gasPlantCapacitiesExportTabName = 'OilAndGasCapacity';

  private dropdownUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasCapacityDropDowns`;
  }

  private capacitiesUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasCapacityDetails`;
  }

  private saveCapacitiesUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/InsertUpdateOilAndGasCapacity`;
  }

  private deleteCapacitiesUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/RemoveOilAndGasCapacityDetails`;
  }

  private cascadingFilterUrl(): string {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasCapacityCascadingFilter`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllGasPlantCapacitiesData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.gasPlantCapacitiesExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `gas-plant-capacities${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  private parseJsonResponse(response: any): any {
    let parsed = response;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return { statusMessage: parsed };
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      parsed = parsed[0];
    }
    return parsed;
  }

  private parseDropdownResponse(response: DropdownColumn[]): GasPlantCapacitiesResponse {
    const developmentAreaItem = response.find(item => item.columnName === 'DevelopmentArea');
    const facilityNameItem = response.find(item => item.columnName === 'FacilityName');
    const facilityTypeItem = response.find(item => item.columnName === 'FacilityType');
    const gasTakeAwayItem = response.find(item => item.columnName === 'GasTakeAway' || item.columnName === 'GasTakeaway');
    const oilTakeAwayItem = response.find(item => item.columnName === 'OilTakeAway' || item.columnName === 'OilTakeaway');
    const subDevAreaItem= response.find(item => item.columnName === 'SubDevelopmentArea');

    return {
      developmentAreas: developmentAreaItem?.values || [],
      facilityNames: facilityNameItem?.values || [],
      facilityTypes: facilityTypeItem?.values || [],
      gasTakeAways: gasTakeAwayItem?.values || [],
      oilTakeAways: oilTakeAwayItem?.values || [],
      SubDevelopmentArea: subDevAreaItem?.values || [],
    };
  }

  async getAllDropdownOptions(): Promise<GasPlantCapacitiesResponse> {
    try {
      const requestBody: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), requestBody)
      );      
      const parsedResponse = this.parseDropdownResponse(response);

      return parsedResponse;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getDropdownOptions(requestBody: GasPlantCapacitiesRequest): Promise<GasPlantCapacitiesResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), requestBody)
      );
      
      const parsedResponse = this.parseDropdownResponse(response);
      
      return parsedResponse;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getDropdownOptions');
      
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getFacilityTypes(): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      const facilityTypeItem = response.find(item => item.columnName === 'FacilityType');
      const facilityTypes = facilityTypeItem?.values || [];
      
      return facilityTypes;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityTypes');
      return [];
    }
  }

  async getDevelopmentAreas(): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      
      const devAreaItem = response.find(item => item.columnName === 'DevelopmentArea');
      const developmentAreas = devAreaItem?.values || [];
      
      return developmentAreas;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getDevelopmentAreas');
      return [];
    }
  }

  async getFacilityNames(): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );
      
      const facilityNameItem = response.find(item => item.columnName === 'FacilityName');
      const facilityNames = facilityNameItem?.values || [];
      
      return facilityNames;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityNames');
      return [];
    }
  }

  async getDevelopmentAreasByFacilityType(facilityType: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: facilityType,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      
      const devAreaItem = response.find(item => item.columnName === 'DevelopmentArea');
      const developmentAreas = devAreaItem?.values || [];
      
      return developmentAreas;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getDevelopmentAreasByFacilityType');
      return [];
    }
  }

  async getDevelopmentAreasByFacilityName(facilityName: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: facilityName,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );
      
      const devAreaItem = response.find(item => item.columnName === 'DevelopmentArea');
      const developmentAreas = devAreaItem?.values || [];
      
      return developmentAreas;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getDevelopmentAreasByFacilityName');
      return [];
    }
  }

  async getFacilityTypesByDevelopmentArea(developmentArea: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: developmentArea,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      
      const facilityTypeItem = response.find(item => item.columnName === 'FacilityType');
      const facilityTypes = facilityTypeItem?.values || [];
      
      return facilityTypes;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityTypesByDevelopmentArea');      return [];
    }
  }

  async getFacilityTypesByFacilityName(facilityName: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: facilityName,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      
      const facilityTypeItem = response.find(item => item.columnName === 'FacilityType');
      const facilityTypes = facilityTypeItem?.values || [];
      
      return facilityTypes;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityTypesByFacilityName');
      return [];
    }
  }

  async getFacilityNamesByDevelopmentArea(developmentArea: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: developmentArea,
        facilityName: null,
        facilityType: null,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );
      
      const facilityNameItem = response.find(item => item.columnName === 'FacilityName');
      const facilityNames = facilityNameItem?.values || [];
      
      return facilityNames;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityNamesByDevelopmentArea');
      return [];
    }
  }

  async getFacilityNamesByFacilityType(facilityType: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: null,
        facilityName: null,
        facilityType: facilityType,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );

      
      const facilityNameItem = response.find(item => item.columnName === 'FacilityName');
      const facilityNames = facilityNameItem?.values || [];
      
      return facilityNames;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilityNamesByFacilityType');
      return [];
    }
  }

  async getFacilitiesByDevAreaAndType(developmentArea: string, facilityType: string): Promise<string[]> {
    try {
      const payload: GasPlantCapacitiesRequest = {
        developmentArea: developmentArea,
        facilityName: null,
        facilityType: facilityType,
        gasTakeAway: null,
        oilTakeAway: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<DropdownColumn[]>(this.dropdownUrl(), payload)
      );
      
      const facilityNameItem = response.find(item => item.columnName === 'FacilityName');
      const facilities = facilityNameItem?.values || [];
      
      return facilities;
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getFacilitiesByDevAreaAndType');
      return [];
    }
  }

  async getFacilitiesByDevArea(developmentArea: string, facilityType?: string): Promise<string[]> {
    if (facilityType) {
      return this.getFacilitiesByDevAreaAndType(developmentArea, facilityType);
    }
    return this.getFacilityNamesByDevelopmentArea(developmentArea);
  }

  async getFacilitiesByType(facilityType: string, developmentArea?: string): Promise<string[]> {
    if (developmentArea) {
      return this.getFacilitiesByDevAreaAndType(developmentArea, facilityType);
    }
    return this.getFacilityNamesByFacilityType(facilityType);
  }

  async getCapacitiesWithPagination(filters?: Partial<GasPlantCapacitiesDetailRequest>): Promise<{
    capacities: GasPlantCapacity[];
    nextRowNumber: number | null;
  }> {
    try {
      const payload: GasPlantCapacitiesDetailRequest = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        facilityType: filters?.facilityType || null,
        gasTakeAway: filters?.gasTakeAway || null,
        oilTakeAway: filters?.oilTakeAway || null,
        capacityType: filters?.capacityType || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<GasPlantCapacitiesDetailResponse>(this.capacitiesUrl(), payload)
      );
      
      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return {
          capacities: response.gasItem || [],
          nextRowNumber: (response as any).nextRowNumber ?? null
        };
      }

      return {
        capacities: [],
        nextRowNumber: null
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getCapacitiesWithPagination');
      return {
        capacities: [],
        nextRowNumber: null
      };
    }
  }

  async getCascadingFilterOptions(filters?: Partial<GasPlantCapacitiesCascadingFilterRequest>): Promise<GasPlantCapacitiesCascadingFilterResponse> {
    try {
      const payload: GasPlantCapacitiesCascadingFilterRequest = {
        developmentArea: filters?.developmentArea || null,
        facilityName: filters?.facilityName || null,
        facilityType: filters?.facilityType || null,
        gasTakeAway: filters?.gasTakeAway || null,
        oilTakeAway: filters?.oilTakeAway || null,
        capacityType: filters?.capacityType || null
      };

      const response = await firstValueFrom(
        this.http.post<GasPlantCapacitiesCascadingFilterResponse>(this.cascadingFilterUrl(), payload)
      );

      return response || [];
    } catch (error) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.getCascadingFilterOptions');
      return [];
    }
  }

  async saveCapacity(capacity: GasPlantCapacityRow, action: 'INSERT' | 'UPDATE', userName: string, originalCapacity?: GasPlantCapacityRow): Promise<any> {
    try {
      const serializedDevelopmentArea = capacity.developmentAreaCards && capacity.developmentAreaCards.length > 0
        ? capacity.developmentAreaCards.join(',')
        : capacity.developmentArea || '';

      const body: SaveGasPlantCapacityRequest = {
        action: action,
        developmentArea: serializedDevelopmentArea,
        facilityName: capacity.facilityName || '',
        facilityType: capacity.assetType || '',
        gasTakeAway: capacity.gasTakeAway || null,
        oilTakeAway: capacity.oilTakeAway || null,
        capacityType: capacity.capacityType || '',
        capacity: capacity.capacity || 0,
        comment: capacity.comment || '',
        effectiveDate: capacity.effective || '',
        oldEffectiveDate: action === 'UPDATE' && originalCapacity?.effective ? originalCapacity.effective : '',
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post(this.saveCapacitiesUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      ErrorHandler.handleError(error, 'GasPlantCapacitiesService.saveCapacity');
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }

  async saveMultipleCapacities(capacities: GasPlantCapacityRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const capacity of capacities) {
      const action = capacity.isNew ? 'INSERT' : 'UPDATE';
      const originalCapacity = capacity.original as GasPlantCapacityRow;
      
      const result = await this.saveCapacity(capacity, action, userName, originalCapacity);
      results.push(result);
    }

    return results;
  }

  async deleteCapacity(capacity: GasPlantCapacityRow): Promise<any> {
    if (!capacity.developmentArea || !capacity.assetType || !capacity.capacityType || !capacity.effective) {
      return {
        statusCode: 400,
        statusMessage: 'Required identifiers (developmentArea, facilityType, capacityType, effectiveDate) are missing for deletion.',
        error: true,
      };
    }

    const body: DeleteGasPlantCapacityRequest = {
      developmentArea: capacity.developmentArea,
      facilityName: capacity.facilityName || null,
      facilityType: capacity.assetType,
      gasTakeAway: capacity.gasTakeAway || null,
      oilTakeAway: capacity.oilTakeAway || null,
      capacityType: capacity.capacityType,
      effectiveDate: capacity.effective
    };

    try {
      const response = await firstValueFrom(
        this.http.post(this.deleteCapacitiesUrl(), body, {
          responseType: 'text' as 'json',
        })
      );

      const parsed = this.parseJsonResponse(response);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode,
        statusMessage: result.message,
        error: !result.success,
        ...parsed,
      };
    } catch (error: any) {
      const rawError = error?.error ?? error;
      const parsed = this.parseJsonResponse(rawError);
      const result = ErrorHandler.parseApiResult(parsed);
      return {
        statusCode: result.statusCode || error?.status || 500,
        statusMessage: result.message,
        error: true,
      };
    }
  }

  async deleteMultipleCapacities(capacities: GasPlantCapacityRow[]): Promise<any[]> {
    const results: any[] = [];
    
    for (const capacity of capacities) {
      if (capacity.isNew) {
        continue;
      }
      
      const result = await this.deleteCapacity(capacity);
      results.push(result);
    }

    return results;
  }
}

export type { 
  GasPlantCapacitiesRequest,
  GasPlantCapacitiesResponse,
  GasPlantCapacitiesDetailRequest,
  GasPlantCapacitiesDetailResponse,
  GasPlantCapacity,
  SaveGasPlantCapacityRequest,
  DeleteGasPlantCapacityRequest,
  GasPlantCapacityRow,
  DropdownColumn,
  GasPlantCapacitiesCascadingFilterRequest,
  GasPlantCapacitiesCascadingFilterResponse,
  FilterColumn
} from './interfaces/gas-plant-capacities.interfaces';